import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { logout } from '../features/authentication';
import { getJwt } from './jwt';
import { pageTeardownSignal } from './pageTeardown';

const API_BASE_URL = window.getApiUrl();

const defaultMaxRetryCount = 6;
const defaultWriteRetryCount = 0;
const retryStatuses: number[] = [500, 502, 503, 504];

type RetryFn = (error: { response: { status: number } }) => boolean;

const handleRetry = <T = unknown>(
  makePromise: () => Promise<AxiosResponse<T>>,
  maxRetryCount: number,
  retryCondition: RetryFn
) => {
  const intervalMs = 1000;

  return new Promise<AxiosResponse<T>>((resolve, reject) => {
    const retry = (tryCount: number) => {
      makePromise().then(
        response => {
          resolve(response);
        },
        error => {
          if (tryCount > maxRetryCount || !retryCondition(error)) {
            reject(error);
          } else {
            setTimeout(() => {
              retry(tryCount + 1);
            }, tryCount * intervalMs);
          }
        }
      );
    };

    retry(1);
  });
};

const createRetryConditionFn =
  (statuses: number[]): RetryFn =>
  (error: { response: { status: number } }): boolean =>
    error.response && statuses.findIndex((s: number) => s === error.response.status) !== -1;

const withTeardownSignal = (config?: AxiosRequestConfig): AxiosRequestConfig =>
  config?.signal ? config : { ...config, signal: pageTeardownSignal() };

export function createApiClient(baseUrl: string) {
  const axiosInstance = axios.create({
    baseURL: baseUrl,
    withCredentials: true,
    transformRequest: ([] as any[]).concat(
      axios.defaults.transformRequest,
      (data: unknown, headers: Record<string, string>) => {
        headers.Authorization = getJwt() ?? '';
        return data;
      }
    ),
    timeout: 60 * 1000, // default 60s timeout, override if needed, 0 = never
  });

  axiosInstance.interceptors.response.use(
    response => response,
    error => {
      const status = error.response?.status;

      if (status === 401) {
        logout();
      }
      return Promise.reject(error);
    }
  );

  return {
    get: <T = unknown>(
      url: string,
      config?: AxiosRequestConfig,
      additionalRetryStatuses: number[] = [],
      optionalMaxRetryCount: number = defaultMaxRetryCount
    ): Promise<AxiosResponse<T>> => {
      return handleRetry(
        () => axiosInstance.get<T>(url, withTeardownSignal(config)),
        optionalMaxRetryCount,
        createRetryConditionFn(retryStatuses.concat(additionalRetryStatuses))
      );
    },

    post: <T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
      additionalRetryStatuses: number[] = [],
      optionalMaxRetryCount: number = defaultWriteRetryCount
    ): Promise<AxiosResponse<T>> => {
      return handleRetry(
        () => axiosInstance.post<T>(url, data, withTeardownSignal(config)),
        optionalMaxRetryCount,
        createRetryConditionFn(retryStatuses.concat(additionalRetryStatuses))
      );
    },

    put: <T = unknown>(
      url: string,
      data?: unknown,
      config?: AxiosRequestConfig,
      additionalRetryStatuses: number[] = [],
      optionalMaxRetryCount: number = defaultWriteRetryCount
    ): Promise<AxiosResponse<T>> => {
      return handleRetry(
        () => axiosInstance.put<T>(url, data, withTeardownSignal(config)),
        optionalMaxRetryCount,
        createRetryConditionFn(retryStatuses.concat(additionalRetryStatuses))
      );
    },

    delete: <T = unknown>(
      url: string,
      config?: AxiosRequestConfig,
      additionalRetryStatuses: number[] = [],
      optionalMaxRetryCount: number = defaultWriteRetryCount
    ): Promise<AxiosResponse<T>> => {
      return handleRetry(
        () => axiosInstance.delete<T>(url, withTeardownSignal(config)),
        optionalMaxRetryCount,
        createRetryConditionFn(retryStatuses.concat(additionalRetryStatuses))
      );
    },

    getConfig: () => {
      return axiosInstance.defaults;
    },
  };
}

const apiClient = createApiClient(API_BASE_URL);

export const apiV2Client = createApiClient(`${API_BASE_URL}-v2`);

export default apiClient;
